import { useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import type { EventListItemDto } from '@/services/platform/api/model';
import { getEvents } from './events';

export interface UseEventGroupEventsResult {
  events: EventListItemDto[];
  isLoading: boolean;
}

/**
 * The other events of one group (contract v1.12 §3.0) — the booking flow's "Where" step
 * options when an event belongs to a group (each edition has its own venue/menu/price/
 * description). No fetch without a `groupId` — most events are a group of one.
 * `allowsBooking: true` + `includeRestricted: false` mirrors `WhichSheet`'s choice: inside a
 * booking flow, an edition the caller can't actually book shouldn't be offered.
 */
export function useEventGroupEvents(
  groupId?: string | null,
  options?: { excludeEventId?: string }
): UseEventGroupEventsResult {
  const locale = useLocaleStore(state => state.locale);
  const excludeEventId = options?.excludeEventId;
  const [events, setEvents] = useState<EventListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!groupId) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getEvents(
      { groupId, allowsBooking: true, includeRestricted: false, excludeExternal: true, pageSize: 50 },
      locale
    )
      .then(result => {
        if (cancelled) return;
        const items = (result?.items ?? []).filter(
          (event): event is EventListItemDto & { id: string } => !!event.id && event.id !== excludeEventId
        );
        setEvents(items);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, excludeEventId, locale]);

  return { events, isLoading };
}
